/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation } from 'react-query'

import {
  patchElasticsearchProject,
  patchObservabilityProject,
  patchSecurityProject,
  patchWorkplaceAIProject,
} from '@modules/project-admin-api/v1/callers'
import type {
  PatchElasticsearchProjectRequest,
  PatchObservabilityProjectRequest,
  PatchSecurityProjectRequest,
  PatchWorkplaceAiProjectRequest,
  ProjectId,
} from '@modules/project-admin-api/v1/types'
import type { QueryFunctionReturnType } from '@modules/query/types'
import type { AnyAdminProject } from '@modules/project-admin-api/types'
import { queryClient } from '@modules/query'
import type { ProjectApiError, ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'

import { makeQueryKey } from './helpers'

/**
 * Returns the appropriate hook for the provided project type.
 */
export function getPatchProjectMutationHook(
  projectType: ProjectTypeWithWorkplaceAI,
):
  | typeof usePatchElasticsearchProjectMutation
  | typeof usePatchObservabilityProjectMutation
  | typeof usePatchSecurityProjectMutation
  | typeof usePatchWorkplaceAIProjectMutation {
  switch (projectType) {
    case 'observability':
      return usePatchObservabilityProjectMutation
    case 'security':
      return usePatchSecurityProjectMutation
    case 'workplaceai':
      return usePatchWorkplaceAIProjectMutation
    default:
      return usePatchElasticsearchProjectMutation
  }
}

const usePatchElasticsearchProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof patchElasticsearchProject>,
    ProjectApiError,
    { id: ProjectId; body: PatchElasticsearchProjectRequest }
  >({
    mutationFn: ({ id, body }) =>
      patchElasticsearchProject({
        pathParameters: { id },
        body,
      }),
    onSuccess: onProjectPatched,
  })

const usePatchObservabilityProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof patchObservabilityProject>,
    ProjectApiError,
    { id: ProjectId; body: PatchObservabilityProjectRequest }
  >({
    mutationFn: ({ id, body }) =>
      patchObservabilityProject({
        pathParameters: { id },
        body,
      }),
    onSuccess: onProjectPatched,
  })

const usePatchSecurityProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof patchSecurityProject>,
    ProjectApiError,
    { id: ProjectId; body: PatchSecurityProjectRequest }
  >({
    mutationFn: ({ id, body }) =>
      patchSecurityProject({
        pathParameters: { id },
        body,
      }),
    onSuccess: onProjectPatched,
  })

const usePatchWorkplaceAIProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof patchWorkplaceAIProject>,
    ProjectApiError,
    { id: ProjectId; body: PatchWorkplaceAiProjectRequest }
  >({
    mutationFn: ({ id, body }) =>
      patchWorkplaceAIProject({
        pathParameters: { id },
        body,
      }),
    onSuccess: onProjectPatched,
  })

const onProjectPatched = (project: AnyAdminProject) => {
  const listQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'adminListElasticsearchProjects',
        observability: 'adminListObservabilityProjects',
        security: 'adminListSecurityProjects',
        workplaceai: 'adminListWorkplaceAIProjects',
      } as const
    )[project.type],
  )
  queryClient.invalidateQueries(listQueryKey)

  const detailQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'adminGetElasticsearchProject',
        observability: 'adminGetObservabilityProject',
        security: 'adminGetSecurityProject',
        workplaceai: 'adminGetWorkplaceAIProject',
      } as const
    )[project.type],
    project.id,
  )
  queryClient.setQueryData(detailQueryKey, project)
}
